import { useRef, useState } from 'react'
import { charBeforeCursor } from './arabicKeyboardCore'
import './arabic-keyboard.css'

// Reusable Dawwin Arabic input-assist keyboard -- an input tool AND a learning aid, laid out
// in Arabic alphabetical order (not a physical keyboard layout), each key showing the letter
// large with its learner-transliteration underneath. Deterministic labelling only: the
// transliteration shown on keys is fixed per-letter learning guidance, never an attempt to
// transliterate arbitrary typed text.
//
// getTarget resolves whatever element should receive the insertion -- a plain <input>/
// <textarea> (Lexicon's root field), or a Qirtaas/ProseMirror contenteditable surface (Notes,
// Reflective Journal). document.execCommand('insertText'/'delete') is used for BOTH
// uniformly: it goes through the same native text-insertion path a real keystroke takes,
// which is exactly what both React's controlled-input sync and ProseMirror's own state sync
// listen to -- so one mechanism works for either kind of target without this component
// needing to know anything about TipTap/ProseMirror's internal document model, and without
// duplicating the block-splice technique used elsewhere for structured Qur'an/Hadith nodes
// (this is plain inline text, not a node insertion). execCommand is marked deprecated in the
// HTML spec but remains universally supported and is the standard technique third-party tools
// use to insert into an arbitrary focused editable region.
//
// Characters are inserted ONE AT A TIME, in the order clicked, at whatever the live cursor
// position is -- there is no reversal, reordering, or custom Arabic shaping anywhere in this
// file. Combining marks (harakat) are ordinary Unicode codepoints; placing one right after its
// base letter's codepoint is all that's needed for the browser's own bidi/shaping engine to
// render it attached correctly. See arabicKeyboardCore.ts + tests/arabicKeyboardCore.test.ts
// for the deterministic verification of this ordering.
const ALPHABET:[string,string][]=[
 ['ا','a'],['ب','b'],['ت','t'],['ث','th'],['ج','j'],['ح','ḥ'],['خ','kh'],
 ['د','d'],['ذ','dh'],['ر','r'],['ز','z'],['س','s'],['ش','sh'],['ص','ṣ'],
 ['ض','ḍ'],['ط','ṭ'],['ظ','ẓ'],['ع','ʿ'],['غ','gh'],['ف','f'],['ق','q'],
 ['ك','k'],['ل','l'],['م','m'],['ن','n'],['ه','h'],['و','w'],['ي','y'],
]
const SPECIAL:[string,string][]=[
 ['ة','ah'],['ى','ā'],['ء','ʾ'],['أ','a'],['إ','i'],['آ','ā'],['ؤ','ʾ'],['ئ','ʾ'],
]
// "mark name · what it sounds like" -- the mark itself (large glyph) stays the visually
// prominent part of the key; this label is only the secondary, small-text learning aid.
const HARAKAT:[string,string][]=[
 ['َ','fatḥah · a'],['ِ','kasrah · i'],['ُ','ḍammah · u'],['ْ','sukūn · no vowel'],
 ['ّ','shaddah · double'],['ً','fatḥatayn · an'],['ٍ','kasratayn · in'],['ٌ','ḍammatayn · un'],
]

export default function ArabicKeyboard({getTarget,onClose}:{getTarget:()=>HTMLElement|null;onClose:()=>void}){
 const[attachPreview,setAttachPreview]=useState('')
 const previewTimer=useRef<number|undefined>(undefined)

 // mousedown (not click) with preventDefault keeps the target focused and its selection
 // intact -- the standard technique for a virtual keyboard: a click would otherwise blur the
 // target first and lose the cursor position before the handler ever runs.
 function withTarget(run:()=>void){
  const el=getTarget()
  if(!el)return
  el.focus()
  run()
 }
 function insert(char:string){withTarget(()=>document.execCommand('insertText',false,char))}
 function backspace(){withTarget(()=>document.execCommand('delete'))}

 // Read-only -- never mutates the target. For a plain input/textarea this is just its value
 // and selectionStart. For a contenteditable (Qirtaas/ProseMirror) this reads the live DOM
 // Selection; it only handles the common case (cursor resting inside a text node) and
 // degrades to "no preview" for anything more complex (e.g. cursor at a block boundary)
 // rather than guessing -- the preview is a nice-to-have, never worth risking a DOM write
 // into an editor surface Dawwin doesn't own.
 function precedingChar():string{
  const el=getTarget()
  if(!el)return''
  if(el instanceof HTMLInputElement||el instanceof HTMLTextAreaElement){
   return charBeforeCursor(el.value,el.selectionStart??el.value.length)
  }
  const sel=window.getSelection()
  if(!sel||!sel.anchorNode||sel.anchorNode.nodeType!==Node.TEXT_NODE)return''
  return charBeforeCursor(sel.anchorNode.textContent||'',sel.anchorOffset)
 }
 function showAttachPreview(){
  const preceding=precedingChar()
  if(!preceding)return
  setAttachPreview(preceding)
  clearTimeout(previewTimer.current)
  previewTimer.current=window.setTimeout(()=>setAttachPreview(''),900)
 }

 function key(char:string,label:string,{harakah=false}:{harakah?:boolean}={}){
  return <button type="button" key={char} className={`arabic-key${harakah?' arabic-key-harakah':''}`} onMouseDown={e=>{e.preventDefault();if(harakah)showAttachPreview();insert(char)}}>
   <span className="arabic-key-letter">{char}</span>
   <span className="arabic-key-label">{label}</span>
  </button>
 }
 return <div className="arabic-keyboard" role="group" aria-label="Arabic input keyboard">
  <div className="arabic-key-row">{ALPHABET.map(([c,l])=>key(c,l))}</div>
  <div className="arabic-key-row arabic-key-row-special">{SPECIAL.map(([c,l])=>key(c,l))}</div>
  <div className="arabic-harakat-preview" aria-live="polite">{attachPreview&&<>Attaching to <span lang="ar" dir="rtl">{attachPreview}</span></>}</div>
  <div className="arabic-key-row arabic-key-row-harakat">{HARAKAT.map(([c,l])=>key(c,l,{harakah:true}))}</div>
  <div className="arabic-keyboard-controls">
   <button type="button" className="arabic-key arabic-key-wide" onMouseDown={e=>{e.preventDefault();insert(' ')}}>Space</button>
   <button type="button" className="arabic-key" onMouseDown={e=>{e.preventDefault();backspace()}} aria-label="Backspace">⌫</button>
   <button type="button" className="arabic-key" onClick={onClose} aria-label="Close keyboard">✕</button>
  </div>
 </div>
}

// Shared toggle button, so every integration (Lexicon, Notes, Reflective Journal) looks and
// behaves identically rather than each styling its own.
export function ArabicKeyboardToggle({open,onToggle}:{open:boolean;onToggle:()=>void}){
 return <button type="button" className={`arabic-keyboard-toggle${open?' active':''}`} onClick={onToggle} title={open?'Close Arabic keyboard':'Open Arabic keyboard'} aria-label="Toggle Arabic keyboard" aria-pressed={open}>
  <span className="arabic-keyboard-toggle-glyph">ع</span>
  <span className="arabic-keyboard-toggle-label">Keyboard</span>
 </button>
}